import React, { useState, useEffect, useCallback } from 'react';
import apiFetch from '../../../../utils/api';
import Preloader from '../../../../components/Preloader/Preloader';
import Pagination from '../../../../components/Pagination/Pagination';
import './AdminStyles.css';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [counts, setCounts] = useState({ total: 0, active: 0, admin: 0, platformBalance: 0, filteredBalanceSum: 0 });
  const [editUser, setEditUser] = useState(null);

  const [editForm, setEditForm] = useState({ email: '', role: 'user', isActive: true, password: '' });
  const [filterType, setFilterType] = useState(''); // '', '24h', '7d', 'older'

  const [withdrawUser, setWithdrawUser] = useState(null);
  const [withdrawForm, setWithdrawForm] = useState({ amount: '', paymentMethod: 'EasyPaisa', accountNumber: '', accountTitle: '', bankName: '' });
  const [acting, setActing] = useState(null);
  const [msg, setMsg] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const fetchData = useCallback(() => {
    setLoading(true);
    let url = `/api/admin/users?page=${page}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (filterType) url += `&filter=${filterType}`;

    
    apiFetch(url).then(r => r.json()).then(res => { 
      setUsers(res.users); 
      setTotalPages(res.totalPages);
      setCounts({ 
        total: res.totalCount, 
        active: res.activeCount, 
        admin: res.adminCount,
        platformBalance: res.platformBalance || 0,
        filteredBalanceSum: res.filteredBalanceSum || 0
      });

      setLoading(false); 
    }).catch(() => setLoading(false));
  }, [page, search, filterType]);


  useEffect(fetchData, [fetchData]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const handleFilterChange = (type) => {
    setPage(1);
    setFilterType(prev => prev === type ? '' : type);
  };


  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({ email: u.email, role: u.role, isActive: u.isActive, password: '' });
    setMsg('');
  };

  const saveEdit = async () => {
    setActing(editUser.id);
    const res = await apiFetch(`/api/admin/users/${editUser.id}/update`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editForm),
    });
    const d = await res.json();
    setActing(null);
    if (d.success) { setEditUser(null); fetchData(); }
    else setMsg(d.message);
  };

  const openWithdraw = async (u) => {
    setWithdrawUser(u);
    try {
      const res = await apiFetch(`/api/admin/users/${u.id}/withdraw`);
      const data = await res.json();
      if (data.success && data.paymentInfo) {
        setWithdrawForm({
          amount: '',
          paymentMethod: data.paymentInfo.paymentMethod || '',
          accountNumber: data.paymentInfo.accountNumber || '',
          accountTitle: data.paymentInfo.accountTitle || '',
          bankName: ''
        });
      } else {
        setWithdrawForm({ amount: '', paymentMethod: '', accountNumber: '', accountTitle: '', bankName: '' });
      }
    } catch {
      setWithdrawForm({ amount: '', paymentMethod: '', accountNumber: '', accountTitle: '', bankName: '' });
    }
    setMsg('');
  };

  const isInvalidAccount = (f) => {
    const fields = [f.paymentMethod, f.accountNumber, f.accountTitle];
    return !fields.every(Boolean) || fields.some(x => ['empty', '-', 'n/a'].includes(x.toString().toLowerCase().trim()));
  };

  const saveWithdraw = async () => {
    if (isInvalidAccount(withdrawForm)) return setMsg('Cannot payout: Missing or invalid account details.');
    if (!withdrawForm.amount || withdrawForm.amount <= 0) return setMsg('Enter valid amount.');
    
    setActing(withdrawUser.id);
    const res = await apiFetch(`/api/admin/users/${withdrawUser.id}/withdraw`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(withdrawForm),
    });
    const d = await res.json();
    setActing(null);
    if (d.success) { setWithdrawUser(null); fetchData(); }
    else setMsg(d.message);
  };

  const handleDelete = async (id, username) => {
    if (!window.confirm(`Delete user "${username}"? This cannot be undone.`)) return;
    setActing(id);
    const res = await apiFetch(`/api/admin/users/${id}/delete`, { method: 'DELETE' });
    const d = await res.json();
    setActing(null);
    if (d.success) fetchData();
    else alert(d.message);
  };


  return (
    <div className="admin-page">
      <div className="admin-breadcrumb">🏠 / Admin / Users</div>
      <h2 className="admin-title">User Management</h2>

      <div className="admin-stats-bar">
        <div className="admin-stat-chip blue">{counts.total} Total</div>
      </div>



      {/* Search Bar + Today Balance Filter */}
      <div style={{ 
        display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' 
      }}>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8, flex: 1, minWidth: 250 }}>
          <input
            type="text"
            placeholder="🔍 Search by ID, username, or email..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            style={{
              flex: 1, padding: '10px 14px', borderRadius: 8, border: '1px solid var(--awp-border)',
              fontSize: 14, background: 'var(--awp-surface)', boxShadow: 'var(--awp-shadow-sm)',
              color: 'var(--awp-text)'
            }}
          />
          <button type="submit" className="admin-btn admin-btn--edit" style={{ padding: '10px 18px' }}>
            Search
          </button>
          {search && (
            <button type="button" className="admin-btn admin-btn--reject" style={{ padding: '10px 14px' }} onClick={clearSearch}>
              ✕ Clear
            </button>
          )}
        </form>
        <div style={{ display: 'flex', gap: 8 }}>
          <button 
            className={`admin-btn ${filterType === '24h' ? 'admin-btn--approve' : 'admin-btn--edit'}`}
            style={{ padding: '8px 14px', fontSize: 13, whiteSpace: 'nowrap' }}
            onClick={() => handleFilterChange('24h')}
          >
            🕒 24h
          </button>
          <button 
            className={`admin-btn ${filterType === '7d' ? 'admin-btn--approve' : 'admin-btn--edit'}`}
            style={{ padding: '8px 14px', fontSize: 13, whiteSpace: 'nowrap' }}
            onClick={() => handleFilterChange('7d')}
          >
            📅 7 Days
          </button>
          <button 
            className={`admin-btn ${filterType === 'older' ? 'admin-btn--approve' : 'admin-btn--edit'}`}
            style={{ padding: '8px 14px', fontSize: 13, whiteSpace: 'nowrap' }}
            onClick={() => handleFilterChange('older')}
          >
            📜 Older
          </button>
        </div>
      </div>


      {search && (
        <div style={{ marginBottom: 16, padding: '8px 14px', background: 'var(--awp-info-soft)', borderRadius: 8, fontSize: 13, color: 'var(--awp-info)' }}>
          Showing results for: <strong>"{search}"</strong>
        </div>
      )}
      {filterType && (
        <div style={{ marginBottom: 16, padding: '8px 14px', background: 'var(--awp-success-soft)', borderRadius: 8, fontSize: 13, color: 'var(--awp-success)' }}>
          💰 Showing users with income in: <strong>{filterType === '24h' ? 'Last 24 Hours' : filterType === '7d' ? 'Last 7 Days' : 'Older than 24h'}</strong>
        </div>
      )}


      <div className="admin-card">
        <div className="admin-card-header">Registered Users</div>
        <div className="admin-card-body">
          {loading ? (
            <Preloader message="Fetching platform users..." fullScreen={false} />
          ) : (
            <>
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Username</th>
                      <th>Email</th>
                      <th>Balance</th>
                      <th>Account #</th>
                      <th>Level</th>
                      <th>Payment Method</th>
                      <th>Team</th>
                      <th>Last Withdrawal</th>
                      <th>Actions</th>
                    </tr>

                  </thead>
                  <tbody>
                    {users.map(row => (
                      <tr key={row.id}>
                        <td>{row.id}</td>
                        <td><strong>{row.username}</strong></td>
                        <td>{row.email}</td>
                        <td><span className="user-balance-chip">Rs. {(row.balance || 0).toLocaleString()}</span></td>
                        <td>{row.accountNumber || <span style={{ color: 'var(--awp-text-subtle)' }}>—</span>}</td>
                        <td><span className="status-badge approved">{row.userLevel}</span></td>
                        <td>{row.paymentMethod || <span style={{ color: 'var(--awp-text-subtle)' }}>—</span>}</td>
                        <td><strong>{row.team || 'N/A'}</strong></td>
                        <td style={{ fontSize: '12px', color: 'var(--awp-text-muted)' }}>{row.latestTransaction}</td>
                        <td>
                          <div className="admin-actions-row">
                            <button className="admin-btn admin-btn--approve" onClick={() => openWithdraw(row)} disabled={acting === row.id}>Withdraw</button>
                            <button className="admin-btn admin-btn--edit" onClick={() => openEdit(row)} disabled={acting === row.id}>Edit</button>
                            <button className="admin-btn admin-btn--reject" onClick={() => handleDelete(row.id, row.username)} disabled={acting === row.id}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Pagination 
                currentPage={page} 
                totalPages={totalPages} 
                onPageChange={setPage} 
              />
            </>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editUser && (
        <div className="admin-modal-overlay" onClick={() => setEditUser(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Edit User: {editUser.username}</h3>
              <button className="admin-modal-close" onClick={() => setEditUser(null)}>✕</button>
            </div>
            {msg && <div className="admin-modal-msg">{msg}</div>}
            <div className="admin-modal-body">
              <label>Email</label>
              <input type="email" value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} />

              <label>Role</label>
              <select value={editForm.role} onChange={e => setEditForm({...editForm, role: e.target.value})}>
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>

              <label>Active</label>
              <select value={editForm.isActive ? 'true' : 'false'} onChange={e => setEditForm({...editForm, isActive: e.target.value === 'true'})}>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>

              <label>Reset Password (leave empty to keep)</label>
              <input type="text" placeholder="New password" value={editForm.password} onChange={e => setEditForm({...editForm, password: e.target.value})} />
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn admin-btn--approve" onClick={saveEdit} disabled={acting === editUser.id}>
                {acting === editUser.id ? 'Saving...' : 'Save Changes'}
              </button>
              <button className="admin-btn admin-btn--edit" onClick={() => setEditUser(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Withdraw Modal */}
      {withdrawUser && (
        <div className="admin-modal-overlay" onClick={() => setWithdrawUser(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Manual Withdrawal: {withdrawUser.username}</h3>
              <button className="admin-modal-close" onClick={() => setWithdrawUser(null)}>✕</button>
            </div>
            <div style={{padding: '0 20px'}}>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ color: 'var(--awp-text-muted)' }}>Available Balance: <strong style={{ color: 'var(--awp-success)' }}>Rs. {(withdrawUser.balance || 0).toLocaleString()}</strong></div>
                {withdrawUser.todayBalance > 0 && (
                  <div style={{ color: 'var(--awp-warning)', background: 'var(--awp-warning-soft)', padding: '2px 10px', borderRadius: 6, fontSize: 13, fontWeight: 600 }}>
                    💰 Today: Rs. {(withdrawUser.todayBalance || 0).toLocaleString()}
                  </div>
                )}
              </div>
              <div style={{ marginTop: 6, fontSize: 12, color: 'var(--awp-text-subtle)' }}>
                Level: <strong>{withdrawUser.userLevel}</strong>
              </div>
            </div>
            {msg && <div className="admin-modal-msg">{msg}</div>}
            <div className="admin-modal-body">
              <label>Amount (PKR)</label>
              <input type="number" placeholder="2000" value={withdrawForm.amount} onChange={e => setWithdrawForm({...withdrawForm, amount: e.target.value})} />

              <label>Payment Method</label>
              <div style={{ padding: '10px', background: 'var(--awp-surface-2)', borderRadius: '4px', marginBottom: '15px', color: 'var(--awp-text)' }}>
                {withdrawForm.paymentMethod || 'Empty'}
              </div>

              <label>Account Number</label>
              <div style={{ padding: '10px', background: 'var(--awp-surface-2)', borderRadius: '4px', marginBottom: '15px', color: 'var(--awp-text)' }}>
                {withdrawForm.accountNumber || 'Empty'}
              </div>

              <label>Account Title</label>
              <div style={{ padding: '10px', background: 'var(--awp-surface-2)', borderRadius: '4px', marginBottom: '15px', color: 'var(--awp-text)' }}>
                {withdrawForm.accountTitle || 'Empty'}
              </div>
            </div>
            <div className="admin-modal-footer">
              <button 
                className="admin-btn admin-btn--approve" 
                onClick={saveWithdraw} 
                disabled={acting === withdrawUser.id || isInvalidAccount(withdrawForm)}
              >
                {acting === withdrawUser.id ? 'Processing...' : 'Approve & Payout'}
              </button>
              <button className="admin-btn admin-btn--edit" onClick={() => setWithdrawUser(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
